import type { LocalizedText } from '../permissions.ts';

/**
 * Padrões de tela do Estúdio de estilos (ADR 0128, fase 4): a lista fechada de jeitos de montar
 * uma tela. Toda tela nova, pedida por prompt, usa um deles; o texto de "quando usar" vai para a
 * IA (docs/guia-visual/padroes-de-tela.md, gerado daqui) e para o estúdio.
 */
export interface ScreenPattern {
  id: string;
  name: LocalizedText;
  /** Quando escolher este padrão, numa frase. */
  whenToUse: LocalizedText;
  /** As peças de `apps/web/src/components/` que montam a tela. */
  pieces: readonly string[];
}

const P = (
  id: string,
  pt: [string, string],
  en: [string, string],
  pieces: string[],
): ScreenPattern => ({
  id,
  name: { 'pt-BR': pt[0], en: en[0] },
  whenToUse: { 'pt-BR': pt[1], en: en[1] },
  pieces,
});

export const SCREEN_PATTERNS: readonly ScreenPattern[] = [
  P(
    'dashboard',
    ['Painel', 'Abertura de uma área: os números do dia, o que pede ação e os atalhos.'],
    ['Dashboard', 'Opening screen of an area: today’s numbers, what needs action and shortcuts.'],
    ['PageHeader', 'StatCard', 'MiniChart', 'Panel'],
  ),
  P(
    'list',
    [
      'Lista',
      'Muitos itens do mesmo tipo para procurar, filtrar e abrir (clientes, pedidos, produtos).',
    ],
    ['List', 'Many items of one kind to search, filter and open (customers, orders, products).'],
    ['ListDetail', 'ListToolbar', 'SearchBox', 'Table', 'StateView'],
  ),
  P(
    'record',
    ['Registro', 'Um item aberto: os dados principais, a situação, as abas e o histórico dele.'],
    ['Record', 'One open item: its main data, status, tabs and history.'],
    ['RecordLayout', 'FactList', 'Tabs', 'Badge'],
  ),
  P(
    'board',
    [
      'Quadro',
      'Itens que andam por etapas e mudam de situação (funil de vendas, chamados, produção).',
    ],
    ['Board', 'Items moving through stages (sales funnel, tickets, production).'],
    ['Board', 'Badge'],
  ),
  P(
    'agenda',
    ['Agenda', 'Compromissos com dia e hora: visitas, reservas, turnos, consultas.'],
    ['Agenda', 'Appointments with day and time: visits, bookings, shifts.'],
    ['WeekAgenda'],
  ),
  P(
    'gallery',
    [
      'Galeria',
      'Itens que se reconhecem pela imagem ou por um cartão (catálogo, imóveis, modelos).',
    ],
    ['Gallery', 'Items recognised by an image or a card (catalogue, properties, templates).'],
    ['CardGrid', 'StateView'],
  ),
  P(
    'wizard',
    [
      'Cadastro em etapas',
      'Um cadastro longo que fica mais fácil em passos (admissão, abertura de conta, orçamento).',
    ],
    ['Step-by-step form', 'A long form that is easier in steps (onboarding, quote, signup).'],
    ['StepsLayout', 'Field', 'Button'],
  ),
  P(
    'conversation',
    ['Conversas', 'Troca de mensagens com clientes ou com a equipe, em conversas separadas.'],
    ['Conversations', 'Messages with customers or the team, in separate threads.'],
    ['ConversationLayout', 'StateView'],
  ),
  P(
    'report',
    [
      'Relatório',
      'Números por período ou por grupo, com totais, gráfico e a tabela para conferir.',
    ],
    ['Report', 'Numbers by period or group, with totals, a chart and the table to check.'],
    ['MiniChart', 'ValueTable', 'TotalRow', 'NumCell'],
  ),
  P(
    'settings',
    [
      'Configurações',
      'Preferências que se ligam, desligam ou escolhem uma vez e valem para todos.',
    ],
    ['Settings', 'Preferences switched on, off or chosen once for everyone.'],
    ['SettingsSection', 'SettingRow', 'Switch'],
  ),
  P(
    'document',
    [
      'Documento impresso',
      'O que sai em papel ou PDF: orçamento, recibo, ordem de serviço, relatório para assinar.',
    ],
    ['Printed document', 'What goes to paper or PDF: quote, receipt, work order.'],
    ['PrintDocument', 'ValueTable'],
  ),
  P(
    'login',
    ['Entrar', 'A tela de entrada do sistema. Já vem pronta na base: não se cria outra.'],
    ['Sign in', 'The sign-in screen. It ships with the base: never build another.'],
    ['LoginPage'],
  ),
];

export const SCREEN_PATTERN_IDS = SCREEN_PATTERNS.map((p) => p.id);

/** O texto que a IA lê para escolher o padrão a cada prompt (uma linha por padrão). */
export function patternsMarkdown(lang: 'pt-BR' | 'en' = 'pt-BR'): string {
  const pt = lang === 'pt-BR';
  const head = pt
    ? [
        '# Padrões de tela',
        '',
        '<!-- Gerado de packages/shared/src/styles/patterns.ts por `pnpm patterns:sync`. Não edite à mão. -->',
        '',
        'Toda tela nova usa um destes padrões (ADR 0128). Escolha pelo "quando usar"; monte com as peças indicadas, de `apps/web/src/components/`. Os estados vazia, carregando, com erro e sem permissão saem do `StateView`.',
        '',
        '| Padrão | Quando usar | Peças |',
        '| --- | --- | --- |',
      ]
    : [
        '# Screen patterns',
        '',
        'Every new screen uses one of these patterns (ADR 0128).',
        '',
        '| Pattern | When to use | Pieces |',
        '| --- | --- | --- |',
      ];
  const rows = SCREEN_PATTERNS.map(
    (p) =>
      `| **${p.name[lang]}** (\`${p.id}\`) | ${p.whenToUse[lang]} | ${p.pieces.map((x) => `\`${x}\``).join(', ')} |`,
  );
  return [...head, ...rows, ''].join('\n');
}
